import type { ReactNode } from "react";
import { Avatar } from "../components/Avatar";
import { fa } from "../i18n/fa";
import { SEAT_AVATAR } from "./felt-geometry";
import type { SeatAvatar } from "./seat-identity";
import { SEAT_NOTICE_DIM, SeatNoticeText, type SeatNotice } from "./seat-notice";

/* ------------------------------------------------------------------ *
 * ONE SEAT PINNED AROUND THE FELT — the seat حکم (`HokmTable`) and بی‌دل (`HeartsTable`) each drew
 * from their own copy until session D (2026-10-09), and پاسور and شلم draw next: an avatar disc in a
 * 2 px ring, a name box under it (or the seat's notice in the name's place), the host's «+» invite
 * badge over a bot's disc.
 *
 * What the tables share lives here — the name box's frame and its truncation included (see
 * `seatNameBoxClass`). What differs is a prop, decided by the table:
 *   `onTurn`       the seat's move — the gold turn-glow, STEADY (`TURN_GLOW_STEADY`), which ALWAYS
 *                  wins over `ringTone` (حکم counts the حاکم choosing trump as on turn too);
 *   `ringTone`     the ring off turn — حکم's team green / red (or its حاکم's gold ring), بی‌دل's neutral;
 *   `aboveAvatar`  a chip floating above the avatar, out of flow (حکم's «حاکم» chip);
 *   `nameSkin`     the name box's padding, type size and colour, in the table's own order (حکم's
 *                  team-coloured caption, بی‌دل's neutral label-md: their K constants are measured
 *                  off those boxes);
 *   `nameMax`      the name box's cap — each table's own `SIDE_NAME_MAX` / `TOP_NAME_MAX`;
 *   `extra`        under the name box (بی‌دل's this-hand points chip, or its pass dots);
 *   `seat`         the chair's ABSOLUTE seat, as `data-seat` on the seat's root (پاسور's — its tests and
 *                  its layout probe find a chair by it); left off, the root carries no attribute;
 *   `avatarCorner` a marker on the avatar's corner, out of flow (the dealer's marker — شلم's, پاسور's).
 * ------------------------------------------------------------------ */

/** YOUR OWN ring's on-turn box-shadow in the strip under the felt — a gold glow that no team or
 *  neutral tone ever covers. It is the declaration UNDER `TURN_GLOW_SETTLE`, which paints over it
 *  from the first frame and holds its last one (as the old pulse covered it). */
export const TURN_GLOW =
  "0 0 16px 4px rgb(var(--c-secondary) / .4), 0 0 0 2px rgb(var(--c-secondary))";

/**
 * THE TURN GLOW SETTLES (card-12, the operator 2026-10-28: «opponents' seat glow steady; on my
 * turn, my ring and playable cards pulse for about 2 s, then hold steady»). The box-shadow pulses
 * that ran for ever were what heated the phones in a game (a box-shadow repaints every frame:
 * 96–97 % of the in-game CPU), so:
 *   `TURN_GLOW_STEADY`  a seat on turn that is not yours — the middle frame of the old pulse
 *                       (theme.css's `--turn-glow-pulse-steady`, 0 0 22.5px at .5), static: no
 *                       animation at all, so not one frame is repainted for it;
 *   `TURN_GLOW_SETTLE`  YOUR ring on your turn — the old pulse once (1.6 s) and up to that same
 *                       middle frame (0.4 s), held (`forwards`). It starts when your turn does —
 *                       the table declares it while it is your move and drops it when it is not —
 *                       and, declared with the same value on every render, never replays inside
 *                       one turn.
 */
export const TURN_GLOW_STEADY = "var(--turn-glow-pulse-steady)";
export const TURN_GLOW_SETTLE = "turn-glow-pulse-settle 2s ease-in-out forwards";
/** The same steady frame drawn TIGHT (0 0 6px at .5) — شلم's own ring while a trick is held. */
export const TURN_GLOW_STEADY_TIGHT = "var(--turn-glow-pulse-tight-steady)";

/**
 * YOUR RING RE-PULSES ON EVERY TURN OF YOURS — `TURN_GLOW_SETTLE` under the name of the move slot your
 * turn is in (card-12 review). A CSS animation restarts only when it is dropped or RENAMED, and two
 * turns of yours can follow each other with no frame off turn in between: you take a trick and lead
 * the next, حکم's call and then your first lead, بی‌دل's last pass and then your 2♣, شلم's discard and
 * then your lead. So the name alternates between two identical keyframes (`…-b`, theme.css) by the
 * slot's parity — a trick in play (`trickNumber`), -1 the phase before it (the call, the pass, the
 * discard), -2 شلم's auction before that — and two turns in a row are always in consecutive slots.
 * Within one turn the slot, so the declaration, never changes. A table also drops the settle while a
 * finished trick is held (`useTrickHold` — the hand is not live yet; the ring shows the steady frame,
 * never the inline ring under it), so the lead after a trick you took re-pulses when the hand does.
 */
export function turnGlowSettle(phase: string, trickNumber: number, tight = false): string {
  const slot = phase === "playing" ? trickNumber : phase === "bidding" ? -2 : -1;
  const name = tight ? "turn-glow-pulse-tight" : "turn-glow-pulse-settle";
  return `${name}${slot & 1 ? "-b" : ""} 2s ease-in-out forwards`;
}

/**
 * THE NAME BOX'S CLASS — the frame and the flow every seat shares, then the table's own skin.
 *
 * The FLOW is the one rule no table may drop: a NAME is cut (`truncate`, at the table's `nameMax`);
 * a side seat's NOTICE wraps instead (`wraps`, see `wrapNotice`). It sits in the frame, ahead of the
 * skin, so a table's skin cannot leave it out. `compactPad` is a zoomed screen's padding — the
 * tightest for a notice, whose text is the one thing here that is never cut — and null on a regular
 * screen, where the table's skin pads the box its own way. The frame, the flow and the skin's slot
 * are where both tables always had them, so their boxes kept every class in the same order.
 * بی‌دل's hidden top-seat twin asks for the same class, so the twin and the seat cannot differ.
 */
export function seatNameBoxClass(
  nameSkin: (compactPad: string | null) => string,
  compact: boolean,
  notice: boolean,
  wraps: boolean,
): string {
  const flow = wraps ? "whitespace-normal break-words text-center" : "truncate";
  const compactPad = compact ? (notice ? "px-1" : "px-1.5") : null;
  return `m-0 inline-block ${flow} box-border rounded-full border ${nameSkin(compactPad)}`;
}

/**
 * A pinned seat's column — the seat's, and the one بی‌دل's hidden top-seat twin (`TopSeatTwin`) is
 * measured on, so the two can never stack differently.
 *
 * `min-w-[52px]`: never narrower than the «حاکم» chip (51.5 px until 2026-10-08; now ≤ the avatar,
 * 31.6 px — the box kept its 52 px, so nothing else moved), centred over the avatar. A short name
 * («Reza», «بات ۲», «Al») let a side seat's avatar hug the box's outer edge, 16 px from the
 * screen's: on حکم the chip stuck out 2.4–3.8 px onto the felt's gold ring (staging, 2026-10-03,
 * measured on both sides), on بی‌دل the avatar sat 4.2–8.6 px off the felt's side against حکم's 12
 * (operator, 2026-10-08: «use the same spacing as حکم»).
 */
export const SEAT_STACK = "flex min-w-[52px] flex-col items-center";
/** The avatar ring's UPPER-RIGHT corner: where `avatarCorner` sits — out of flow, letting a tap
 *  through — and where a table puts the same marker on your own ring in its strip (the dealer's,
 *  `dealer-marker.tsx`). The ring under it must be a positioned box.
 *  THE ONE TOP CORNER FREE IN EVERY STATE (card-67 B4, the operator: «The dealer marker sits on the
 *  official tick when a player has one. Move it to whichever top corner of the avatar is free»): it
 *  sat on the LOWER-right, where `Avatar` stamps an official account's tick (`bottom-0 right-0`); the
 *  upper-LEFT is the host's «+» on a bot's chair (`justify-end` under rtl); and every chip over an
 *  avatar — the «حاکم» chip, شلم's bid / «پاس» badge, پاسور's «یار» — is centred above it, 3 px over
 *  the ring. So the upper-right, its top AT the ring's (`top-0`, not `-top-0.5`: those chips keep
 *  their 3 px of air over it) and 4 px past its side (`-right-1`, as before: پاسور's corner slot is
 *  measured on that overhang, `CORNER_SLOT_MAX`). */
export const AVATAR_CORNER = "pointer-events-none absolute top-0 -right-1";
/** The column's gap: tighter on a zoomed screen (`useCompactTable`). */
export const seatGap = (compact: boolean): string => (compact ? "gap-1" : "gap-1.5");

export interface TableSeatProps {
  /** Where the seat sits around the felt — Tailwind position classes. */
  posClass: string;
  /** The seat's face — the real user's photo/preset when known, else the positional preset. */
  avatar: SeatAvatar;
  /** The seat's name, painted unless a notice takes its place. */
  name: string;
  /** What has just happened to this seat, said on it — see `seat-notice.tsx`. */
  notice?: SeatNotice | null;
  /** It is this seat's move: the gold turn-glow (steady), which always wins over `ringTone`. */
  onTurn: boolean;
  /** A zoomed screen (`useCompactTable`): the smaller avatar and gap — and the tightest name padding
   *  for a notice, whose text is the one thing here that is never cut (`seatNameBoxClass`). */
  compact?: boolean;
  /** FEATURE 3 — set only for a BOT chair the host may fill: tapping it opens the friend picker. */
  onInvite?: (() => void) | undefined;
  /** The ring (a `box-shadow`) off turn. */
  ringTone: string;
  /** A chip floating above the avatar — out of flow, so it never adds to the seat's height (which
   *  keeps a top seat from reaching down over its played card). */
  aboveAvatar?: ReactNode;
  /** The name box's SKIN, after the frame and the flow `TableSeat` draws itself (`seatNameBoxClass`):
   *  its padding — `compactPad` on a zoomed screen, else the table's own — type size and colour, in
   *  the table's order. */
  nameSkin: (compactPad: string | null) => string;
  /** The NAME box's max width (CSS). */
  nameMax: string;
  /** A SIDE seat: a notice in the name's place is capped at `nameMax` too and WRAPS («در حال /
   *  اتصال») instead of being cut — uncapped it reached the side cards under any text zoom above
   *  ~1.03 or on a felt ≤ 300 px (2026-10-08, the Redmi 13C report: «names still sit on the
   *  cards»). The TOP seat leaves it off: its notice has the felt's width to itself, and a second
   *  line would make the seat taller, onto the top card. */
  wrapNotice?: boolean;
  /** Marks the root `data-side-seat`: بی‌دل's notices column measures its room under a text zoom
   *  against its side seats (`useNoticeRoom`, HeartsTable). Off by default, so a table that does not
   *  measure (حکم) keeps its DOM exactly as it was. */
  markSideSeat?: boolean;
  /** Marks the root `data-top-seat`: the same measure reads بی‌دل's TOP seat too (card-a5 item 10 —
   *  on the shortest felts the column reaches up to it). Passed on that one seat only — never its
   *  invisible twin (`TopSeatTwin`), which is no seat. Off by default, like `markSideSeat`. */
  markTopSeat?: boolean;
  /** Under the name box. */
  extra?: ReactNode;
  /** The chair's absolute seat, as `data-seat` on the root — undefined (حکم, بی‌دل): no attribute. */
  seat?: number;
  /** A small marker on the avatar ring's upper-right corner (`AVATAR_CORNER`) — out of flow, so it
   *  adds nothing to the seat's height or width, and it lets a tap through (شلم's dealer marker,
   *  پاسور's). Absent, the seat renders exactly as it did without the prop. */
  avatarCorner?: ReactNode;
}

export function TableSeat({
  posClass,
  avatar,
  name,
  notice = null,
  onTurn,
  compact = false,
  onInvite,
  ringTone,
  aboveAvatar = null,
  nameSkin,
  nameMax,
  wrapNotice = false,
  markSideSeat = false,
  markTopSeat = false,
  extra = null,
  seat,
  avatarCorner = null,
}: TableSeatProps): ReactNode {
  // a side seat's notice: capped like its name and wrapped, never cut (see `wrapNotice`)
  const noticeWraps = notice !== null && wrapNotice;
  const ring = onTurn ? TURN_GLOW_STEADY : ringTone;
  return (
    <div
      data-seat={seat}
      className={`absolute z-20 ${SEAT_STACK} ${seatGap(compact)} ${posClass}`}
      data-side-seat={markSideSeat ? "" : undefined}
      data-top-seat={markTopSeat ? "" : undefined}
    >
      <div
        className="relative flex items-center justify-center rounded-full"
        style={{
          padding: 2,
          // border-box + a flex centre keep the avatar exactly inside the 2px ring:
          // the padding never pushes it out, and the (block-level) avatar sits flush
          // with no inline baseline gap — so it can't overflow the ring on Android.
          boxSizing: "border-box",
          boxShadow: ring,
          opacity: notice?.dim === true ? SEAT_NOTICE_DIM : undefined,
        }}
      >
        {aboveAvatar ? (
          <div
            className="absolute left-1/2 -translate-x-1/2"
            style={{ bottom: "calc(100% + 3px)" }}
          >
            {aboveAvatar}
          </div>
        ) : null}
        <Avatar
          avatarId={avatar.avatarId}
          avatarPhotoId={avatar.avatarPhotoId}
          size={compact ? SEAT_AVATAR.compact : SEAT_AVATAR.regular}
          official={avatar.official}
        />
        {onInvite ? (
          // The host may replace this bot with a friend — a tap-target over the disc + a «+» badge.
          <button
            type="button"
            onClick={onInvite}
            aria-label={fa.online.seatInviteTitle()}
            // `appearance-none bg-transparent border-0` is load-bearing: Tailwind preflight is OFF,
            // so without them iOS WebKit paints the native push-button chrome (an opaque light fill)
            // right over the bot's avatar — the reported white/blank disc.
            className="absolute inset-0 flex appearance-none items-start justify-end rounded-full border-0 bg-transparent active:scale-95"
          >
            <span className="-mr-1 -mt-1 flex h-4 w-4 items-center justify-center rounded-full bg-secondary text-[11px] font-black leading-none text-ink-on-gold shadow-md">
              +
            </span>
          </button>
        ) : null}
        {avatarCorner ? (
          // the upper-right corner: clear of the official tick (lower-right) and the «+» (upper-left)
          <div className={AVATAR_CORNER}>{avatarCorner}</div>
        ) : null}
      </div>
      {/* `dir="auto"`: inside this RTL shell a Latin name is its own LTR run, and an RTL box
          truncates at its inline END — the visual left, where an LTR run STARTS — so without it a
          long Latin name lost its beginning («…ilian Feather»), not its end. */}
      <span
        dir="auto"
        className={seatNameBoxClass(nameSkin, compact, notice !== null, noticeWraps)}
        style={notice && !wrapNotice ? undefined : { maxWidth: nameMax }}
      >
        {notice ? <SeatNoticeText notice={notice} /> : name}
      </span>
      {extra}
    </div>
  );
}
